import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowLeft, Orbit, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DAYS, defaultSchedule, fmtHour } from "../lib/schedule";

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Berlin",
];

const HOURS = Array.from({ length: 25 }, (_, i) => i); // 0-24

export default function CampaignNew() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [tz, setTz] = useState("America/New_York");
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [schedule, setSchedule] = useState(defaultSchedule());
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPN, setLoadingPN] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/calling/phone-numbers");
        setPhoneNumbers(data.items || []);
        if ((data.items || []).length > 0) setPhoneNumberId(data.items[0].id);
      } catch (e) {
        toast.error("Could not load phone numbers");
      } finally {
        setLoadingPN(false);
      }
    };
    load();
    // Load clients (admin-only — silent fail if 403/empty)
    api.get("/admin/clients").then(({ data }) => {
      setClients((data.items || []).filter((c) => c.status !== "archived"));
    }).catch(() => {});
  }, []);

  const setDayField = (dayId, field, value) => {
    setSchedule((s) => ({
      ...s,
      [String(dayId)]: { ...s[String(dayId)], [field]: value },
    }));
  };

  const applyWeekdaysPreset = () => {
    setSchedule(defaultSchedule());
  };

  const clearAll = () => {
    const empty = {};
    DAYS.forEach((d) => {
      empty[String(d.id)] = { enabled: false, start_hour: 9, end_hour: 17 };
    });
    setSchedule(empty);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!phoneNumberId) return toast.error("Select an outbound phone number");
    const anyEnabled = Object.values(schedule).some((d) => d.enabled);
    if (!anyEnabled) return toast.error("Enable at least one day");
    for (const d of DAYS) {
      const cfg = schedule[String(d.id)];
      if (cfg.enabled && cfg.start_hour >= cfg.end_hour) {
        return toast.error(`${d.label}: start must be before end`);
      }
    }
    setLoading(true);
    try {
      const { data } = await api.post("/campaigns", {
        name: name.trim(),
        phone_number_id: phoneNumberId,
        timezone: tz,
        max_attempts: maxAttempts,
        client_id: clientId || null,
        schedule,
      });
      toast.success("Campaign created");
      nav(`/campaigns/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create campaign");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="campaign-new-page" className="max-w-3xl">
      <Link
        to="/campaigns"
        className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-white/40 hover:text-white mb-6"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to campaigns
      </Link>
      <h1 className="text-3xl font-medium tracking-tight text-white mb-2">New Campaign</h1>
      <p className="text-sm text-white/50 mb-8">
        Configure Ashley's calling window per day and retry rules. You'll upload prospects next.
      </p>

      <div className="bg-[#121214] border border-white/10 rounded-xl p-6 mb-6 flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FFB000]/30 to-[#FFB000]/5 border border-[#FFB000]/20 flex items-center justify-center">
          <Orbit className="w-6 h-6 text-[#FFB000]" strokeWidth={1.5} />
        </div>
        <div>
          <div className="text-sm font-medium text-white">Ashley Demo Booker</div>
          <div className="text-xs text-white/50 mt-0.5">
            Outbound AI sales agent · assigned to every new campaign
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="campaign-new-form">
        <div>
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Campaign Name
          </label>
          <input
            data-testid="campaign-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="Q1 Roofers Outreach"
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 placeholder:text-white/30 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Outbound Phone Number
          </label>
          {loadingPN ? (
            <div className="flex items-center gap-2 text-sm text-white/40">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Loading numbers…
            </div>
          ) : phoneNumbers.length === 0 ? (
            <div className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-md p-3">
              No phone numbers found. Provision one with your calling provider, then refresh.
            </div>
          ) : (
            <select
              data-testid="campaign-phone-number-select"
              value={phoneNumberId}
              onChange={(e) => setPhoneNumberId(e.target.value)}
              className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
            >
              {phoneNumbers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.number || p.name || p.id}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
            Assign to Client <span className="text-white/30 normal-case tracking-normal">(optional — leave blank for Repliant Arc's own outreach)</span>
          </label>
          <select
            data-testid="campaign-client-select"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
          >
            <option value="">— None (internal Repliant Arc campaign) —</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name} ({c.slug})</option>
            ))}
          </select>
          {clientId && (
            <div className="text-[11px] text-white/45 mt-1.5">
              Calls + bookings from this campaign will appear in this client's portal.
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
              Timezone
            </label>
            <select
              data-testid="campaign-timezone-select"
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
            >
              {TIMEZONES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em] mb-2 block">
              Max Attempts / Prospect
            </label>
            <input
              data-testid="campaign-max-attempts-input"
              type="number"
              min="1"
              max="10"
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(parseInt(e.target.value || "3", 10))}
              className="w-full bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-3.5 py-2.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
            />
          </div>
        </div>

        {/* Per-day schedule */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs font-medium text-white/50 uppercase tracking-[0.1em]">
              Calling Schedule (per day)
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="schedule-preset-weekdays"
                onClick={applyWeekdaysPreset}
                className="text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-[#FFB000] transition-colors"
              >
                Mon–Fri 9–17
              </button>
              <span className="text-white/20">·</span>
              <button
                type="button"
                data-testid="schedule-clear-all"
                onClick={clearAll}
                className="text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-red-400 transition-colors"
              >
                Clear all
              </button>
            </div>
          </div>

          <div
            data-testid="schedule-per-day"
            className="bg-[#121214] border border-white/10 rounded-xl divide-y divide-white/5 overflow-hidden"
          >
            {DAYS.map((d) => {
              const cfg = schedule[String(d.id)];
              return (
                <div
                  key={d.id}
                  data-testid={`schedule-day-row-${d.short.toLowerCase()}`}
                  className={`flex items-center gap-4 px-4 py-3 ${cfg.enabled ? "" : "opacity-50"}`}
                >
                  {/* Toggle */}
                  <button
                    type="button"
                    role="switch"
                    aria-checked={cfg.enabled}
                    data-testid={`schedule-day-toggle-${d.short.toLowerCase()}`}
                    onClick={() => setDayField(d.id, "enabled", !cfg.enabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${
                      cfg.enabled ? "bg-[#FFB000]" : "bg-white/10"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${
                        cfg.enabled ? "left-[18px]" : "left-0.5"
                      }`}
                    />
                  </button>

                  {/* Day label */}
                  <div className="w-24 flex-shrink-0">
                    <div className="text-sm text-white font-medium">{d.label}</div>
                  </div>

                  {/* Hours */}
                  {cfg.enabled ? (
                    <div className="flex-1 flex items-center gap-2 flex-wrap">
                      <select
                        data-testid={`schedule-day-start-${d.short.toLowerCase()}`}
                        value={cfg.start_hour}
                        onChange={(e) =>
                          setDayField(d.id, "start_hour", parseInt(e.target.value, 10))
                        }
                        className="bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-2.5 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
                      >
                        {HOURS.slice(0, 24).map((h) => (
                          <option key={h} value={h}>
                            {fmtHour(h)}
                          </option>
                        ))}
                      </select>
                      <span className="text-white/40 text-sm">→</span>
                      <select
                        data-testid={`schedule-day-end-${d.short.toLowerCase()}`}
                        value={cfg.end_hour}
                        onChange={(e) =>
                          setDayField(d.id, "end_hour", parseInt(e.target.value, 10))
                        }
                        className="bg-[#0a0a0b] border border-white/10 rounded-md text-white text-sm px-2.5 py-1.5 font-mono focus:outline-none focus:ring-1 focus:ring-[#FFB000] focus:border-[#FFB000]"
                      >
                        {HOURS.slice(1).map((h) => (
                          <option key={h} value={h}>
                            {fmtHour(h)}
                          </option>
                        ))}
                      </select>
                      {cfg.start_hour >= cfg.end_hour && (
                        <span className="text-[11px] text-red-400 font-mono ml-2">
                          start must be before end
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="flex-1 text-xs font-mono uppercase tracking-widest text-white/30">
                      Off
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[11px] text-white/40 mt-2 font-mono">
            Ashley dials only during enabled hours. Timezone above applies to all rows.
          </div>
        </div>

        <div className="pt-4 flex gap-3">
          <button
            data-testid="campaign-create-submit-btn"
            type="submit"
            disabled={loading}
            className="bg-[#FFB000] hover:bg-[#FFC133] text-black font-medium text-sm px-5 py-2.5 rounded-md transition-colors shadow-[0_0_24px_rgba(255,176,0,0.18)] disabled:opacity-60 flex items-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Create Campaign
          </button>
          <Link
            to="/campaigns"
            className="bg-[#18181B] hover:bg-[#27272A] text-zinc-200 border border-white/10 font-medium text-sm px-5 py-2.5 rounded-md transition-colors"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
